import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useBack } from '../lib/back.js';
import type { CoverRequest, TimeRange } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { useAuth, useAccess } from '../context/AuthContext.js';
import { useCoverRequest, useTakeCover, useWithdrawTake, useCancelCover, useNudge } from '../lib/queries.js';
import { PageShell } from '../components/PageShell.js';
import { Icon } from '../components/Icon.js';
import { CapabilityStrip, CoverStatusPill, CoverTimeline } from '../components/CoverBits.js';
import { emitError } from '../lib/toastBus.js';
import { KIND_LABEL, rangeLabel, clockWithin, hhmm } from '../lib/shifts.js';

/** One cover request: what's covered, what isn't, and what you can do about it. */
export function CoverDetailPage() {
  const { id = '' } = useParams();
  const back = useBack('/shifts?tab=cover');
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const { data: req, isLoading, error } = useCoverRequest(orgId, id);

  return (
    <PageShell noNav>
      <div style={{
        padding: '12px 16px', background: theme.surface, borderBottom: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 5,
      }}>
        <button onClick={back} aria-label="Back" style={iconBtn}>
          <Icon name="back" size={18} />
        </button>
        <div style={{ fontSize: 17, fontWeight: 700, color: theme.text }}>Cover request</div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', background: theme.bg, padding: '14px 16px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {isLoading && <div style={muted}>Loading…</div>}
        {error && <div style={muted}>{(error as Error).message}</div>}
        {req && <Detail orgId={orgId} req={req} />}
      </div>
    </PageShell>
  );
}

function Detail({ orgId, req }: { orgId: string; req: CoverRequest }) {
  const live = req.status === 'open';
  const myTakes = req.takes.filter((t) => t.isMine && new Date(t.startsAt) > new Date());
  const partly = req.takes.length > 0;
  const { me } = useAccess();

  return (
    <>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={label}>{KIND_LABEL[req.kind]}</span>
          <CoverStatusPill status={req.status} partly={partly} />
        </div>
        <div style={{ fontSize: 17, fontWeight: 800, color: theme.text }}>{rangeLabel(req.startsAt, req.endsAt)}</div>
        <div style={{ fontSize: 14, color: theme.textDim, marginTop: 2 }}>
          {askedLine(req, me?.id)}
        </div>
        {req.note && <div style={{ fontSize: 13, color: theme.text, marginTop: 8, padding: '8px 10px', background: theme.surface2, borderRadius: 8 }}>“{req.note}”</div>}
        {!req.mine && req.requesterCoveredYou > 0 && (
          <div style={{ fontSize: 13, color: theme.status.service.fg, marginTop: 8 }}>
            {req.requester.displayName} has covered you {req.requesterCoveredYou} time{req.requesterCoveredYou > 1 ? 's' : ''} this year.
          </div>
        )}
      </Card>

      {req.status !== 'cancelled' && (
        <Card>
          <div style={label}>Who's covering</div>
          <CoverTimeline req={req} />
        </Card>
      )}

      {req.impact && req.status !== 'cancelled' && (
        <Card>
          <div style={label}>{req.impact.after ? 'Crew right now' : 'Impact'}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: theme.text, marginBottom: req.impact.after ? 8 : 0 }}>{req.impact.summary}</div>
          {req.impact.after && <CapabilityStrip cap={req.impact.after} />}
        </Card>
      )}

      {live && req.canTake && <TakePanel orgId={orgId} req={req} />}
      {live && !req.mine && !req.canTake && req.gaps.length > 0 && (
        <div style={muted}>You're rostered, away or already covering at that time, or not eligible for this kind of shift.</div>
      )}

      {req.swapId && (
        <div style={muted}>This is one half of a swap — open the swap to undo it.</div>
      )}
      {myTakes.length > 0 && !req.swapId && <MyTakes orgId={orgId} req={req} takes={myTakes} />}

      {req.mine && live && req.candidates && <Candidates orgId={orgId} req={req} />}
      {req.mine && !req.swapId && (live || req.status === 'covered') && new Date(req.endsAt) > new Date() && <CancelButton orgId={orgId} req={req} />}
    </>
  );
}

/** Who asked, worded for the viewer — including a leader asking for someone. */
function askedLine(req: CoverRequest, viewerId: string | undefined): string {
  const by = req.askedBy;
  if (!by) return req.mine ? 'You asked for cover' : `${req.requester.displayName} can't make it`;
  if (by.userId === viewerId) return `You asked for cover for ${req.requester.displayName}${req.requester.hasApp ? '' : ' (no app)'}`;
  if (req.requester.userId === viewerId) return `${by.displayName} asked for cover for you`;
  return `${req.requester.displayName} can't make it · asked by ${by.displayName}`;
}

function TakePanel({ orgId, req }: { orgId: string; req: CoverRequest }) {
  const take = useTakeCover(orgId, req.id);
  const [partOf, setPartOf] = useState<TimeRange | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const free = req.myFreeParts;
  const all = free.length === req.gaps.length && free.every((f, i) => f.startsAt === req.gaps[i]!.startsAt && f.endsAt === req.gaps[i]!.endsAt);

  function run(ranges: TimeRange[]) {
    take.mutate(ranges, {
      // the free hours have changed, so the picked slot and times no longer apply
      onSuccess: () => { setPartOf(null); setFrom(''); setTo(''); },
      onError: (e) => emitError(e instanceof Error ? e.message : 'Could not take it'),
    });
  }

  return (
    <Card>
      <div style={label}>Can you help?</div>
      <button disabled={take.isPending} onClick={() => run(free)} style={primary}>
        {take.isPending ? 'Saving…' : all ? 'I’ll cover all of it' : 'I’ll cover all I can'}
      </button>
      {!all && (
        <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 6 }}>
          You're free for: {free.map((f) => rangeLabel(f.startsAt, f.endsAt)).join(', ')}
        </div>
      )}
      <div style={{ marginTop: 12, fontSize: 13, color: theme.textDim }}>Or just some hours:</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
        {free.map((f) => (
          <button key={f.startsAt} onClick={() => { setPartOf(f); setFrom(hhmm(f.startsAt)); setTo(hhmm(f.endsAt)); }}
            style={chip(partOf?.startsAt === f.startsAt)}>
            {rangeLabel(f.startsAt, f.endsAt)}
          </button>
        ))}
      </div>
      {partOf && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
          <label style={timeLabel}>From <input type="time" value={from} onChange={(e) => setFrom(e.target.value)} style={input} /></label>
          <label style={timeLabel}>to <input type="time" value={to} onChange={(e) => setTo(e.target.value)} style={input} /></label>
          <button disabled={take.isPending} style={secondary} onClick={() => {
            const a = clockWithin(from, partOf.startsAt, partOf.endsAt);
            const b = a && clockWithin(to, a, partOf.endsAt);
            if (!a || !b || b <= a) { emitError('Pick times inside that shift'); return; }
            run([{ startsAt: a, endsAt: b }]);
          }}>Cover these hours</button>
        </div>
      )}
    </Card>
  );
}

function MyTakes({ orgId, req, takes }: { orgId: string; req: CoverRequest; takes: CoverRequest['takes'] }) {
  const withdraw = useWithdrawTake(orgId, req.id);
  return (
    <Card>
      <div style={label}>You're covering</div>
      {takes.map((t) => (
        <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: theme.text, padding: '3px 0' }}>
          <span style={{ flex: 1 }}>{rangeLabel(t.startsAt, t.endsAt)}</span>
          {/* With several pieces, any one can be given back on its own. */}
          {takes.length > 1 && (
            <button disabled={withdraw.isPending} onClick={() => {
              if (confirm(`Withdraw ${rangeLabel(t.startsAt, t.endsAt)}? ${req.requester.displayName} will be told it's open again.`)) withdraw.mutate(t.id);
            }} aria-label={`Withdraw ${rangeLabel(t.startsAt, t.endsAt)}`} style={{ ...secondary, padding: '4px 10px', fontSize: 12 }}>
              Withdraw
            </button>
          )}
        </div>
      ))}
      <button disabled={withdraw.isPending} onClick={() => {
        if (confirm(`Withdraw${takes.length > 1 ? ' all of it' : ''}? ${req.requester.displayName} will be told it's open again.`)) withdraw.mutate('mine');
      }} style={{ ...secondary, marginTop: 8 }}>
        {takes.length > 1 ? 'I can’t do any of it' : 'I can’t do it any more'}
      </button>
    </Card>
  );
}

function Candidates({ orgId, req }: { orgId: string; req: CoverRequest }) {
  const nudge = useNudge(orgId, req.id);
  const [nudged, setNudged] = useState<Set<string>>(new Set());
  const list = req.candidates ?? [];
  return (
    <Card>
      <div style={label}>Ask someone directly</div>
      {list.length === 0 && <div style={muted}>Nobody eligible is free for what's left.</div>}
      {list.slice(0, 8).map((c) => (
        <div key={c.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderTop: `1px solid ${theme.border}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>{c.displayName} <span style={{ fontSize: 12, fontWeight: 500, color: theme.textMuted }}>{c.teamName ?? ''}</span></div>
            <div style={{ fontSize: 12, color: c.needed ? theme.status.service.fg : theme.textMuted }}>
              {[...c.reasons, c.free === 'part' ? 'free for part' : null, c.coveredYou ? `covered ${req.askedBy ? req.requester.displayName.split(' ')[0] : 'you'} ${c.coveredYou}×` : null].filter(Boolean).join(' · ') || 'free'}
            </div>
          </div>
          {!c.hasApp ? <span style={{ fontSize: 12, color: theme.textMuted }}>No app — call</span> : <button
            disabled={nudge.isPending || nudged.has(c.userId)}
            onClick={() => nudge.mutate(c.userId, {
              onSuccess: () => setNudged(new Set(nudged).add(c.userId)),
              onError: (e) => emitError(e instanceof Error ? e.message : 'Could not nudge'),
            })}
            style={{ ...secondary, padding: '6px 12px' }}
          >
            {nudged.has(c.userId) ? 'Asked ✓' : 'Nudge'}
          </button>}
        </div>
      ))}
    </Card>
  );
}

function CancelButton({ orgId, req }: { orgId: string; req: CoverRequest }) {
  const cancel = useCancelCover(orgId, req.id);
  return (
    <button
      disabled={cancel.isPending}
      onClick={() => { if (confirm(req.takes.length ? 'Cancel? Everyone covering will be told they’re not needed.' : 'Cancel this request?')) cancel.mutate(); }}
      style={{ ...secondary, color: theme.status.danger.fg }}
    >
      I can make it after all — cancel request
    </button>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: '12px 14px', borderRadius: 12, background: theme.surface, border: `1px solid ${theme.border}`, flexShrink: 0 }}>{children}</div>;
}

const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: theme.textMuted, marginBottom: 6 };
const muted: React.CSSProperties = { fontSize: 13, color: theme.textMuted, lineHeight: 1.5 };
const timeLabel: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: theme.textDim };
const input: React.CSSProperties = {
  padding: '7px 9px', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface,
  color: theme.text, fontFamily: theme.font, fontSize: 14,
};
const primary: React.CSSProperties = {
  width: '100%', padding: '12px 0', borderRadius: 12, border: 'none', fontSize: 15, fontWeight: 700,
  fontFamily: theme.font, background: theme.accent, color: theme.accentInk, cursor: 'pointer',
};
const secondary: React.CSSProperties = {
  padding: '10px 14px', borderRadius: 10, border: `1px solid ${theme.border}`, fontSize: 13, fontWeight: 700,
  fontFamily: theme.font, background: theme.surface, color: theme.text, cursor: 'pointer',
};
const iconBtn: React.CSSProperties = {
  width: 36, height: 36, border: `1px solid ${theme.border}`, background: theme.surface2, color: theme.text,
  borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
};
const chip = (on: boolean): React.CSSProperties => ({
  padding: '6px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: theme.font,
  border: `1px solid ${on ? theme.accent : theme.border}`, background: on ? theme.accent : theme.surface,
  color: on ? theme.accentInk : theme.textDim,
});
